import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Badge, Button, EmptyState, SearchInput, cn } from '@chopchop-se/ui';
import type { PickingFlowTarget } from '@warehouse/shared';
import { GripVertical, Plus, Route, X } from 'lucide-react';
import { useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * The picking-flow sequence editor (spec §5.8).
 *
 * The list IS the walking route: position 1 is picked first. dnd-kit's keyboard
 * sensor is wired deliberately — this is configured on a warehouse desktop as
 * often as on a tablet, and a drag-only reorder locks out keyboard users
 * entirely.
 */

export interface FlowStepDraft {
  /** Stable id for dnd — the target id, since a target appears at most once. */
  id: string;
  kind: 'area' | 'shelf' | 'bin';
  label: string;
}

export interface FlowStepEditorProps {
  steps: FlowStepDraft[];
  onChange: (steps: FlowStepDraft[]) => void;
  targets: PickingFlowTarget[];
  loadingTargets?: boolean;
}

export function FlowStepEditor({
  steps,
  onChange,
  targets,
  loadingTargets = false,
}: FlowStepEditorProps): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const [query, setQuery] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const chosen = useMemo(() => new Set(steps.map((s) => s.id)), [steps]);
  const available = useMemo(() => {
    const term = query.trim().toLowerCase();
    return targets
      .filter((target) => !chosen.has(target.id))
      .filter((target) => (term ? target.label.toLowerCase().includes(term) : true))
      .slice(0, 200);
  }, [targets, chosen, query]);

  function handleDragEnd(event: DragEndEvent): void {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const from = steps.findIndex((s) => s.id === active.id);
    const to = steps.findIndex((s) => s.id === over.id);
    if (from === -1 || to === -1) return;
    onChange(arrayMove(steps, from, to));
  }

  return (
    <div className="grid gap-6 md:grid-cols-2">
      <section aria-labelledby="flow-steps-heading">
        <h3 id="flow-steps-heading" className="mb-1 text-sm font-semibold text-ink">
          {t('flows.steps')}
        </h3>
        <p className="mb-3 text-xs text-ink-muted">{t('flows.stepsHint')}</p>

        {steps.length === 0 ? (
          <EmptyState bare icon={<Route />} title={t('flows.emptySteps')} />
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={steps.map((s) => s.id)} strategy={verticalListSortingStrategy}>
              <ol className="flex flex-col gap-2" data-testid="flow-steps">
                {steps.map((step, index) => (
                  <SortableStep
                    key={step.id}
                    step={step}
                    position={index + 1}
                    onRemove={() => {
                      onChange(steps.filter((s) => s.id !== step.id));
                    }}
                    onMove={(delta) => {
                      const to = index + delta;
                      if (to < 0 || to >= steps.length) return;
                      onChange(arrayMove(steps, index, to));
                    }}
                  />
                ))}
              </ol>
            </SortableContext>
          </DndContext>
        )}
      </section>

      <section aria-labelledby="flow-targets-heading">
        <h3 id="flow-targets-heading" className="mb-1 text-sm font-semibold text-ink">
          {t('flows.available')}
        </h3>
        <p className="mb-3 text-xs text-ink-muted">{t('flows.availableHint')}</p>

        <SearchInput
          variant="outline"
          value={query}
          onChange={setQuery}
          onClear={() => {
            setQuery('');
          }}
          placeholder={t('flows.search')}
          aria-label={t('flows.search')}
        />

        <ul className="mt-3 flex max-h-96 flex-col gap-1 overflow-y-auto pr-1" data-testid="flow-targets">
          {loadingTargets ? <li className="p-2 text-sm text-ink-muted">…</li> : null}
          {available.map((target) => (
            <li key={target.id}>
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-lg border border-surface-border px-3 py-2 text-left text-sm transition-colors hover:border-brand-400 hover:bg-brand-50/40"
                onClick={() => {
                  onChange([...steps, { id: target.id, kind: target.kind, label: target.label }]);
                }}
              >
                <Plus className="size-3.5 shrink-0 text-brand-600" aria-hidden />
                <span className="truncate">{target.label}</span>
                <Badge tone="neutral" size="sm" className="ml-auto shrink-0">
                  {t(`flows.kind.${target.kind}`)}
                </Badge>
              </button>
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function SortableStep({
  step,
  position,
  onRemove,
  onMove,
}: {
  step: FlowStepDraft;
  position: number;
  onRemove: () => void;
  onMove: (delta: number) => void;
}): ReactNode {
  const { t } = useTranslation('pickingShipping');
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: step.id,
  });

  return (
    <li
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={cn(
        'flex items-center gap-2 rounded-lg border border-surface-border bg-surface px-3 py-2',
        isDragging && 'opacity-60 shadow-lg',
      )}
      data-testid={`flow-step-${position}`}
    >
      <button
        type="button"
        className="cursor-grab touch-none text-ink-muted hover:text-ink"
        aria-label={`${t('flows.steps')} ${step.label}`}
        {...attributes}
        {...listeners}
      >
        <GripVertical className="size-4" aria-hidden />
      </button>
      <span className="w-6 shrink-0 font-mono text-xs text-ink-muted">{position}</span>
      <span className="truncate text-sm">{step.label}</span>
      <Badge tone="neutral" size="sm" className="ml-auto shrink-0">
        {t(`flows.kind.${step.kind}`)}
      </Badge>
      {/* Keyboard fallback that does not depend on drag at all. */}
      <div className="flex shrink-0 gap-0.5">
        <Button
          variant="ghost"
          size="sm"
          aria-label={`${step.label} ↑`}
          onClick={() => {
            onMove(-1);
          }}
        >
          ↑
        </Button>
        <Button
          variant="ghost"
          size="sm"
          aria-label={`${step.label} ↓`}
          onClick={() => {
            onMove(1);
          }}
        >
          ↓
        </Button>
        <Button variant="ghost" size="sm" aria-label={`${step.label} ✕`} onClick={onRemove}>
          <X className="size-3.5" aria-hidden />
        </Button>
      </div>
    </li>
  );
}
